import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode, HttpHeader } from "../../types";
import Input from "@/components/Input";
import { IconClose } from "@velaro/velaro-component-lib";
import { TertiaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib";
import { Toggle } from "@velaro/velaro-component-lib";
import useSubscriptions from "@/hooks/useSubscriptions";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function PostConversationEditor({ node, onUpdate }: Props) {
  const { subscription } = useSubscriptions();

  const canUseComplianceMode = subscription?.enableComplianceMode ?? false;
  const canUsePiiRedaction = subscription?.enablePiiRedaction ?? false;

  return (
    <>
      <FormGroup label="Endpoint URL">
        <Input
          value={node.postUrl || ""}
          placeholder="https://api.example.com/webhook"
          onChange={(postUrl) => onUpdate({ postUrl })}
        />
        <p className="text-xs text-slate-500 mt-1">
          HTTPS required for security
        </p>
      </FormGroup>

      <FormGroup label="Expected Success Code">
        <Input
          type="number"
          value={node.expectedSuccessCode?.toString() || "200"}
          onChange={(value) =>
            onUpdate({ expectedSuccessCode: parseInt(value) || 200 })
          }
        />
        <p className="text-xs text-slate-500 mt-1">
          Response code that confirms successful delivery (typically 200)
        </p>
      </FormGroup>

      <FormGroup label="Authentication Headers">
        <div className="border rounded-lg p-2 mb-4 bg-slate-gray-100 flex flex-col items-center">
          {node.postHeaders &&
            node.postHeaders.map((header: HttpHeader, i: number) => (
              <React.Fragment key={i}>
                <div className="flex w-full items-center gap-1">
                  <Input
                    placeholder="Header Name (e.g., Authorization)"
                    className="my-2 flex grow"
                    value={header.key}
                    onChange={(key) => {
                      const newHeaders = [...node.postHeaders];
                      newHeaders[i] = { ...header, key };
                      onUpdate({ postHeaders: newHeaders });
                    }}
                  />
                  <IconClose
                    onClick={() => {
                      onUpdate({
                        postHeaders: node.postHeaders.filter(
                          (_: HttpHeader, index: number) => index !== i
                        )
                      });
                    }}
                    className="cursor-pointer"
                    style={{ color: "#CBD5E1" }}
                  />
                </div>
                <Input
                  placeholder="Header Value (e.g., Bearer {{apiKey}})"
                  className="my-2"
                  value={header.value}
                  onChange={(value) => {
                    const newHeaders = [...node.postHeaders];
                    newHeaders[i] = { ...header, value };
                    onUpdate({ postHeaders: newHeaders });
                  }}
                />
                <div className="my-2 border border-slate-300 w-full"></div>
              </React.Fragment>
            ))}
          <TertiaryGrayButton
            prefixIcon={<IconPlus />}
            label="Add Header"
            onClick={() => {
              onUpdate({
                postHeaders: [...(node.postHeaders || []), { key: "", value: "" }]
              });
            }}
          />
        </div>
      </FormGroup>

      <div className="border-t border-slate-200 my-4" />

      <FormGroup label="Include in Payload">
        <div className="space-y-3">
          <Toggle
            toggled={node.includeTranscript ?? true}
            changeToggle={() =>
              onUpdate({ includeTranscript: !node.includeTranscript })
            }
            label="Conversation transcript"
          />
          <Toggle
            toggled={node.includePreChatSurvey ?? false}
            changeToggle={() =>
              onUpdate({ includePreChatSurvey: !node.includePreChatSurvey })
            }
            label="Pre-chat survey responses"
          />
          <Toggle
            toggled={node.includeIntroBotMessages ?? false}
            changeToggle={() =>
              onUpdate({ includeIntroBotMessages: !node.includeIntroBotMessages })
            }
            label="Intro bot messages"
          />
          <Toggle
            toggled={node.includeAgentSurvey ?? false}
            changeToggle={() =>
              onUpdate({ includeAgentSurvey: !node.includeAgentSurvey })
            }
            label="Agent survey responses"
          />
          <Toggle
            toggled={node.includeContactProperties ?? false}
            changeToggle={() =>
              onUpdate({
                includeContactProperties: !node.includeContactProperties
              })
            }
            label="Contact properties"
          />
          <Toggle
            toggled={node.includeConversationProperties ?? false}
            changeToggle={() =>
              onUpdate({
                includeConversationProperties: !node.includeConversationProperties
              })
            }
            label="Conversation properties"
          />
        </div>
      </FormGroup>

      {canUsePiiRedaction && (
        <>
          <div className="border-t border-slate-200 my-4" />

          <FormGroup label="PII Redaction">
            <p className="text-xs text-slate-500 mb-3">
              Remove sensitive data before posting
            </p>
            <div className="space-y-3">
              <Toggle
                toggled={node.stripEmails ?? false}
                changeToggle={() => onUpdate({ stripEmails: !node.stripEmails })}
                label="Strip email addresses"
              />
              <Toggle
                toggled={node.stripPhoneNumbers ?? false}
                changeToggle={() =>
                  onUpdate({ stripPhoneNumbers: !node.stripPhoneNumbers })
                }
                label="Strip phone numbers"
              />
            </div>
          </FormGroup>
        </>
      )}

      {canUseComplianceMode && (
        <>
          <div className="border-t border-slate-200 my-4" />

          <FormGroup label="Compliance Mode">
            <div className="space-y-3">
              <Toggle
                toggled={node.complianceMode ?? false}
                changeToggle={() => {
                  if (node.complianceMode) {
                    // Turning off - also reset acknowledgment
                    onUpdate({
                      complianceMode: false,
                      complianceAcknowledged: false
                    });
                  } else {
                    onUpdate({ complianceMode: true });
                  }
                }}
                label="Delete transcript after successful POST"
              />

              {node.complianceMode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                  <h4 className="font-semibold text-amber-800 mb-2">
                    Compliance Acknowledgment Required
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1 mb-3">
                    <li>
                      • Transcript will be <strong>permanently deleted</strong>{" "}
                      after receiving the configured success response code
                    </li>
                    <li>
                      • Only metadata (timestamps, duration, agent IDs) will be
                      retained for audit purposes
                    </li>
                    <li>
                      • Velaro cannot recover transcripts once deleted
                    </li>
                    <li>
                      • You are responsible for ensuring your endpoint properly
                      stores the data before returning a success response
                    </li>
                  </ul>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={node.complianceAcknowledged ?? false}
                      onChange={() =>
                        onUpdate({
                          complianceAcknowledged: !node.complianceAcknowledged
                        })
                      }
                      className="mt-1"
                    />
                    <span className="text-sm text-amber-800">
                      I understand and accept these terms. My organization takes
                      responsibility for data handling once the success response
                      is received.
                    </span>
                  </label>
                </div>
              )}
            </div>
          </FormGroup>
        </>
      )}

      <div className="border-t border-slate-200 my-4" />

      <FormGroup label="Failure Handling">
        <div className="space-y-3">
          <Toggle
            toggled={node.alertOnFailure ?? false}
            changeToggle={() =>
              onUpdate({ alertOnFailure: !node.alertOnFailure })
            }
            label="Send alert on POST failure"
          />
          {node.alertOnFailure && (
            <div className="mt-2">
              <label className="text-sm text-slate-600">Alert email</label>
              <Input
                type="text"
                value={node.failureAlertEmail || ""}
                placeholder="admin@company.com"
                onChange={(failureAlertEmail) => onUpdate({ failureAlertEmail })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Email notification will be sent if the POST fails or returns a
                non-success response code
              </p>
            </div>
          )}
        </div>
      </FormGroup>

      {!canUseComplianceMode && !canUsePiiRedaction && (
        <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 mt-4">
          <p className="text-sm text-slate-600">
            <strong>Upgrade available:</strong> Enable compliance mode
            (auto-delete after posting) and PII redaction with an upgraded
            subscription plan.
          </p>
        </div>
      )}
    </>
  );
}
